import { MembershipItemType, MembershipPurchaseType, RuleCategory } from './index';

/**
 * Membership purchase type to rule category mapping
 * Used for API calls to membership lifecycle forms endpoint
 */
export const PURCHASE_TYPE_TO_RULE_CATEGORY = {
  MembershipPurchase: ['STANDARD_NEW'],
  MembershipUpgrade: ['STANDARD_UPGRADE'],
  MembershipRenewal: ['STANDARD_RENEW'],
  MembershipPostTermRenewal: ['AFTER_TERM_RENEW'],
  MembershipRejoin: ['REJOIN_RENEW'],
  MembershipRenewalUpgrade: ['UPGRADE_RENEW'],
  MembershipPostTermRenewalUpgrade: ['AFTER_TERM_UPGRADE'],
  MembershipRejoinUpgrade: ['REJOIN_UPGRADE'],
  MembershipRenewalDowngrade: ['DOWNGRADE_RENEW'],
  MembershipPostTermRenewalDowngrade: ['AFTER_TERM_DOWNGRADE'],
  MembershipRejoinDowngrade: ['REJOIN_DOWNGRADE'],
} as const satisfies Record<MembershipPurchaseType, readonly RuleCategory[]>;

/**
 * Legacy membership enums for AngularJS services
 * 
 * @description Central configuration for membership lifecycle actions,
 * purchase types, and their mappings to API rule categories.
 * 
 * Used by:
 * - CartMembershipsService (via DI)
 * - RetailMembershipsController (via DI)
 * - Tests (via direct import)
 * 
 * @todo Deprecate in favor of more specific constants
 */
export const MEMBERSHIP_ENUMS = {
  newmembership: ['new_memberships'],
  renew: [
    'renew',
    'renewUpgrade',
    'renewDowngrade',
    'postTermRenew',
    'postTermRenewUpgrade',
    'postTermRenewDowngrade',
    'rejoin',
    'rejoinUpgrade',
    'rejoinDowngrade',
  ],
  upgradeDowngrade: ['upgrade', 'downgrade'],
  cancel: ['cancel'],
  eligibilityTypes: [
    'new_memberships',
    'renew',
    'renewUpgrade',
    'renewDowngrade',
    'postTermRenew',
    'postTermRenewUpgrade',
    'postTermRenewDowngrade',
    'rejoin',
    'rejoinUpgrade',
    'rejoinDowngrade',
    'upgrade',
    'downgrade',
    'cancel',
  ],
  eligibilityTypesCart: [
    'MembershipPurchase',
    'MembershipUpgrade',
    'MembershipRenewal',
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipRenewalUpgrade',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipRenewalDowngrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
    'MembershipDowngrade',
    'MembershipCancel',
  ],

  MembershipPurchaseTypesToRuleCategoryMap: PURCHASE_TYPE_TO_RULE_CATEGORY,

  MembershipPurchaseTypes: {
    new_memberships: 'MembershipPurchase',
    upgrade: 'MembershipUpgrade',
    renew: 'MembershipRenewal',
    postTermRenew: 'MembershipPostTermRenewal',
    rejoin: 'MembershipRejoin',
    renewUpgrade: 'MembershipRenewalUpgrade',
    postTermRenewUpgrade: 'MembershipPostTermRenewalUpgrade',
    rejoinUpgrade: 'MembershipRejoinUpgrade',
    renewDowngrade: 'MembershipRenewalDowngrade',
    postTermRenewDowngrade: 'MembershipPostTermRenewalDowngrade',
    rejoinDowngrade: 'MembershipRejoinDowngrade',
  },

  upgradeDowngradeCart: ['MembershipUpgrade', 'MembershipDowngrade'],
} as const;

export type MembershipEnums = typeof MEMBERSHIP_ENUMS;

/**
 * Sale channel values for membership form preferences
 *
 * @description The backend expects PascalCase values for membership form sale channels,
 * which differs from the standard camelCase SalesChannelType used elsewhere.
 *
 * Used by:
 * - MembershipFormsCreateController
 * - TemplatesCreateSchedulesCreateFormsController (for event forms)
 */
export const MEMBERSHIP_FORM_SALE_CHANNELS = {
  ONLINE: 'Online',
  POS: 'Pos',
  KIOSK: 'Kiosk',
  INSIDE_SALES_INDIVIDUAL: 'InsideSalesIndividual',
} as const;

export type MembershipFormSaleChannel = typeof MEMBERSHIP_FORM_SALE_CHANNELS[keyof typeof MEMBERSHIP_FORM_SALE_CHANNELS];

/**
 * Reverse map from a lifecycle rule category to its corresponding membership item type.
 * Used to derive the `itemType` for a `CartMembershipOrderForm` from the API's
 * `lifecycleAction` field on a `MembershipLifecycleActionForm`.
 *
 * Categories that share a purchase-type bucket (e.g. early/mid/late-term variants)
 * are mapped to the closest matching item type.
 */
export const RULE_CATEGORY_TO_PURCHASE_TYPE: Partial<Record<RuleCategory, MembershipItemType>> = {
  STANDARD_NEW: 'MembershipPurchase',
  STANDARD_RENEW: 'MembershipRenewal',
  UPGRADE_RENEW: 'MembershipRenewalUpgrade',
  DOWNGRADE_RENEW: 'MembershipRenewalDowngrade',
  STANDARD_UPGRADE: 'MembershipUpgrade',
  EARLY_TERM_UPGRADE: 'MembershipUpgrade',
  MID_TERM_UPGRADE: 'MembershipUpgrade',
  LATE_TERM_UPGRADE: 'MembershipUpgrade',
  STANDARD_DOWNGRADE: 'MembershipDowngrade',
  EARLY_TERM_DOWNGRADE: 'MembershipDowngrade',
  MID_TERM_DOWNGRADE: 'MembershipDowngrade',
  LATE_TERM_DOWNGRADE: 'MembershipDowngrade',
  AFTER_TERM_RENEW: 'MembershipPostTermRenewal',
  AFTER_TERM_UPGRADE: 'MembershipPostTermRenewalUpgrade',
  AFTER_TERM_DOWNGRADE: 'MembershipPostTermRenewalDowngrade',
  REJOIN_RENEW: 'MembershipRejoin',
  REJOIN_UPGRADE: 'MembershipRejoinUpgrade',
  REJOIN_DOWNGRADE: 'MembershipRejoinDowngrade',
  STANDARD_CANCELLATION: 'MembershipCancel',
  EARLY_TERM_CANCELLATION: 'MembershipCancel',
  LATE_TERM_CANCELLATION: 'MembershipCancel',
};

